import { getOrgConfig } from "../../utils/organizationConfigUtil";

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(String(value).replace(/,/g, ''));  // remove commas
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// convert MM/DD/YYYY -> YYYY-MM-DD (for input[type="date"])
const toInputDate = (v) => {
    if (!v) return "";
    if (v === "//") return "";
    if (v.includes("/")) {
        const [mm, dd, yyyy] = v.split("/");
        if (yyyy && mm && dd) return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    }
    // if already yyyy-mm-dd return as-is
    return v;
};

const FieldInput = ({ id, value, readOnly, type = "text", ariaLabel, alignRight }) => {
    let defaultVal = value ?? "";

    if (type === "number") {
        defaultVal = defaultVal.replace(/,/g, "");
    }

    return (
        <input
            id={`field-${id}`}
            type={type}
            readOnly={!!readOnly}
            className={`form-input ${alignRight ? "text-right" : ""} w-48 ${readOnly ? "cursor-not-allowed" : ""}`}
            defaultValue={defaultVal}
            aria-label={ariaLabel || id}
        />
    );
};


const FeeBreakdownControls = ({ Fields = [], fieldValues = {} }) => {

    const BROKER_PERCENT_ID = "389";
    const BROKER_FIXED_ID = "1620";
    const BROKER_TOTAL_ID = "439";
    const SELLER_PAID_ID = "572";
    const INTEREST_DAILY_ID = "333";
    const INTEREST_DAYS_ID = "332";
    const INTEREST_TOTAL_ID = "334";
    const lblPayable = getOrgConfig().labels?.feeText;
    const renderLabel = (text) => <div className="form-label-13 block text-sm pr-2">{text}</div>;

    // Row is a 6-column grid (same layout as before)
    const Row = ({ children, style = {} }) => (
        <div
            className="grid gap-3 items-start py-1"
            style={{
                gridTemplateColumns: "220px 220px 24px 220px 24px 200px 200px",
                ...style,
            }}
        >
            {children}
        </div>
    );

    const findField = (id) => Fields.find((f) => String(f.id) === String(id));

    const renderSingleFieldRow = (fieldId, options = {}) => {
        const f = findField(fieldId) || {};
        const v = fieldValues[fieldId] ?? "";
        const isNumeric = options.numeric || f.datatype === "decimal" || f.datatype === "integer";
        const inputType = f.datatype === "date" ? "date" : "text";
        const readOnly = !!f.readonly;

        return (
            <Row key={fieldId}>
                <div className="col-span-1">{renderLabel(f.name || options.label || fieldId)}</div>

                {/* make input occupy columns 2-6 when single field */}
                <div className="col-span-1">
                    <FieldInput
                        id={fieldId}
                        value={formatCurrency(v) || (v ?? "")}
                        type={inputType}
                        ariaLabel={f.name}
                        alignRight={isNumeric}
                        readOnly={readOnly}
                    />
                </div>
                <div className="col-span-5">&nbsp;</div>
            </Row>
        );
    };

    const renderBrokerRow = () => {
        const pctField = findField(BROKER_PERCENT_ID) || { name: "Broker Fees (%)", readonly: false };
        const fixedField = findField(BROKER_FIXED_ID) || { name: "Broker Fees ($)", readonly: false };
        const totalField = findField(BROKER_TOTAL_ID) || { name: "= Broker Total ($)", readonly: true };
        const sellerPaidField = findField(SELLER_PAID_ID) || { name: "Seller Paid", readonly: true };

        const pctReadOnly = !!pctField.readonly;
        const fixedReadOnly = !!fixedField.readonly;
        const totalReadOnly = !!totalField.readonly;

        return (
            <Row key="broker">
                <div className="col-span-1">{renderLabel("Broker Fees")}</div>

                {/* Percent input in col 2 */}
                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{pctField.name}</label>
                    <FieldInput
                        id={BROKER_PERCENT_ID}
                        value={fieldValues[BROKER_PERCENT_ID] ?? ""}
                        ariaLabel={pctField.name}
                        readOnly={pctReadOnly}
                        type="number"
                    />
                </div>

                {/* + operator in col 3 */}
                <div className="col-span-1" aria-hidden="true">
                    <label className="form-label-13 block text-sm mb-1">&nbsp;</label>
                    <span className="px-2 text-lg font-medium select-none">+</span>
                </div>

                {/* Fixed fee in col 4 */}
                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{fixedField.name}</label>
                    <FieldInput
                        id={BROKER_FIXED_ID}
                        value={formatCurrency(fieldValues[BROKER_FIXED_ID]) || (fieldValues[BROKER_FIXED_ID] ?? "")}
                        type="text"
                        ariaLabel={fixedField.name}
                        alignRight
                        readOnly={fixedReadOnly}
                    />
                </div>

                {/* = operator in col 5 */}
                <div className="col-span-1" aria-hidden="true">
                    <label className="form-label-13 block text-sm mb-1">&nbsp;</label>
                    <span className="px-2 text-lg font-medium select-none">=</span>
                </div>

                {/* Total broker fees in col 6 - read from field, don't compute */}
                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{totalField.name}</label>
                    <FieldInput
                        id={BROKER_TOTAL_ID}
                        value={formatCurrency(fieldValues[BROKER_TOTAL_ID]) || (fieldValues[BROKER_TOTAL_ID] ?? "")}
                        readOnly={totalReadOnly || true} /* always read-only display for computed/total fields */
                        ariaLabel={totalField.name}
                        alignRight
                    />
                </div>

                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{sellerPaidField.name}</label>
                    <FieldInput
                        id={SELLER_PAID_ID}
                        value={formatCurrency(fieldValues[SELLER_PAID_ID]) || (fieldValues[SELLER_PAID_ID] ?? "")}
                        readOnly={true} /* always read-only display for computed/total fields */
                        ariaLabel={sellerPaidField.name}
                        alignRight
                    />
                </div>
            </Row>
        );
    };

    const CREDIT_NAME_ID = "624";
    const CREDIT_AMOUNT_ID = "640";

    const renderCreditRow = () => {
        const creditnameField = findField(CREDIT_NAME_ID) || { name: "Name of the Credit", readonly: false };
        const creditAmountField = findField(CREDIT_AMOUNT_ID) || { name: "Amount", readonly: false };

        return (
            <Row key="broker">
                <div className="col-span-1">{renderLabel("Credit Report")}</div>

                {/* Percent input in col 2 */}
                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{creditnameField.name}</label>
                    <FieldInput
                        id={CREDIT_NAME_ID}
                        value={fieldValues[CREDIT_NAME_ID] ?? ""}
                        ariaLabel={creditnameField.name}
                        type="text"
                    />
                </div>

                <div className="col-span-1">&nbsp;</div>

                {/* Fixed fee in col 4 */}
                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{creditAmountField.name}</label>
                    <FieldInput
                        id={CREDIT_AMOUNT_ID}
                        value={fieldValues[CREDIT_AMOUNT_ID] ?? ""}
                        type="number"
                        ariaLabel={creditAmountField.name}
                        alignRight
                    />
                </div>

                <div className="col-span-3">&nbsp;</div>

            </Row>
        );
    };



    const renderPaidToRow = (descId, paidToId, amountId) => {
        const descField = findField(descId) || {};
        const paidToField = findField(paidToId) || { name: "Paid To Name" };
        const amountField = findField(amountId) || { name: "Amount ($)" };

        const descReadOnly = !!descField.readonly;
        const paidToReadOnly = !!paidToField.readonly;
        const amountReadOnly = !!amountField.readonly;

        return (
            <Row key={`paidto-${descId}`}>
                <div className="col-span-1">{renderLabel('')}</div>

                {/* Description in col 2 */}
                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">&nbsp;</label>
                    <FieldInput
                        id={descId}
                        value={fieldValues[descId] ?? ""}
                        type="text"
                        ariaLabel={descField?.name || descId}
                        readOnly={descReadOnly}
                    />
                </div>

                <div className="col-span-1">&nbsp;</div>

                {/* Paid To in col 3 */}
                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{paidToField.name}</label>
                    <FieldInput
                        id={paidToId}
                        value={fieldValues[paidToId] ?? ""}
                        type="text"
                        ariaLabel={paidToField.name}
                        readOnly={paidToReadOnly}
                    />
                </div>

                {/* empty column (keeps spacing consistent) in col 4 */}
                <div className="col-span-1" />

                {/* Amount in col 5 */}
                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{amountField.name}</label>
                    <FieldInput
                        id={amountId}
                        value={fieldValues[amountId] ?? ""}
                        type="number"
                        ariaLabel={amountField.name}
                        alignRight
                        readOnly={amountReadOnly}
                    />
                </div>

                {/* last column empty to keep layout balanced */}
                <div className="col-span-1" >&nbsp;</div>
            </Row>
        );
    };

    const renderInterestCalcRow = () => {
        const dailyField = findField(INTEREST_DAILY_ID) || { name: "Daily Amount" };
        const daysField = findField(INTEREST_DAYS_ID) || { name: "Number of Days" };
        const totalField = findField(INTEREST_TOTAL_ID) || { name: "Total Amount" };

        const dailyReadOnly = !!dailyField.readonly;
        const daysReadOnly = !!daysField.readonly;
        const totalReadOnly = !!totalField.readonly;

        return (
            <Row key="interest-calc">
                <div className="col-span-1">{renderLabel("Interest")}</div>

                {/* Daily Amount in col 2 */}
                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{dailyField.name}</label>
                    <FieldInput
                        id={INTEREST_DAILY_ID}
                        value={fieldValues[INTEREST_DAILY_ID] ?? ""}
                        type="text"
                        ariaLabel={dailyField.name}
                        alignRight
                        readOnly={dailyReadOnly}
                    />
                </div>

                {/* * operator in col 3 */}
                <div className="col-span-1" aria-hidden="true">
                    <label className="form-label-13 block text-sm mb-1">&nbsp;</label>
                    <span className="px-2 text-lg font-medium select-none">*</span>
                </div>

                {/* Number of Days in col 4 */}
                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{daysField.name}</label>
                    <FieldInput
                        id={INTEREST_DAYS_ID}
                        value={fieldValues[INTEREST_DAYS_ID] ?? ""}
                        type="text"
                        ariaLabel={daysField.name}
                        readOnly={daysReadOnly}
                    />
                </div>

                {/* = operator in col 5 */}
                <div className="col-span-1" aria-hidden="true">
                    <label className="form-label-13 block text-sm mb-1">&nbsp;</label>
                    <span className="px-2 text-lg font-medium select-none">=</span>
                </div>

                {/* Total Amount in col 6 - read from field */}
                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{totalField.name}</label>
                    <FieldInput
                        id={INTEREST_TOTAL_ID}
                        value={formatCurrency(fieldValues[INTEREST_TOTAL_ID]) || (fieldValues[INTEREST_TOTAL_ID] ?? "")}
                        type="text"
                        ariaLabel={totalField.name}
                        alignRight
                        readOnly={totalReadOnly || true}
                    />
                </div>
                <div className="col-span-1" >&nbsp;</div>
            </Row>
        );
    };

    const renderInterestRangeRow = () => {
        const fromField = findField("L244") || { name: "Interest From" };
        const toField = findField("L245") || { name: "Interest To" };

        const fromReadOnly = !!fromField.readonly;
        const toReadOnly = !!toField.readonly;

        return (
            <Row key="interest-range">
                <div className="col-span-1">{renderLabel("Interest Range")}</div>

                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{fromField.name}</label>
                    <FieldInput
                        id={"L244"}
                        value={toInputDate(fieldValues["L244"] ?? "")}
                        type="date"
                        ariaLabel={fromField.name}
                        readOnly={fromReadOnly}
                    />
                </div>

                <div className="col-span-1" />

                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{toField.name}</label>
                    <FieldInput
                        id={"L245"}
                        value={toInputDate(fieldValues["L245"] ?? "")}
                        type="date"
                        ariaLabel={toField.name}
                        readOnly={toReadOnly}
                    />
                </div>

                {/* final two cols left empty for symmetry */}
                <div className="col-span-3" >&nbsp;</div>
            </Row>
        );
    };


    const Discount_Point_ID = "NEWHUD.X1150";
    const DISCOUNT_AMOUNT_ID = "NEWHUD.X1151";

    const renderLOCompRow = () => {
        const dicountPointField = findField(Discount_Point_ID) || { name: "Discount Point", readonly: false };
        const dicountAmountField = findField(DISCOUNT_AMOUNT_ID) || { name: "Amount", readonly: false };

        const pointReadOnly = !!dicountPointField.readonly;
        const amountReadOnly = !!dicountAmountField.readonly;


        return (
            <Row key="broker">
                <div className="col-span-1">{renderLabel(lblPayable)}</div>

                {/* Percent input in col 2 */}
                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{dicountPointField.name}</label>
                    <FieldInput
                        id={Discount_Point_ID}
                        value={fieldValues[Discount_Point_ID] ?? ""}
                        ariaLabel={dicountPointField.name}
                        type="number"
                        readOnly={pointReadOnly}
                    />
                </div>

                <div className="col-span-1">&nbsp;</div>

                {/* Fixed fee in col 4 */}
                <div className="col-span-1">
                    <label className="form-label-13 block text-sm mb-1">{dicountAmountField.name}</label>
                    <FieldInput
                        id={DISCOUNT_AMOUNT_ID}
                        value={formatCurrency(fieldValues[DISCOUNT_AMOUNT_ID]) || (fieldValues[DISCOUNT_AMOUNT_ID] ?? "")}
                        type="text"
                        ariaLabel={dicountAmountField.name}
                        alignRight
                        readOnly={amountReadOnly}
                    />
                </div>

                <div className="col-span-3">&nbsp;</div>

            </Row>
        );
    };

    return (
        <>
            {/* Inject CSS directly inside the JS file */}
            <style>
                {`
                    /* Chrome, Safari, Edge, Opera */
                    input[type="number"]::-webkit-inner-spin-button,
                    input[type="number"]::-webkit-outer-spin-button {
                        -webkit-appearance: none;                        
                    }

                    /* Firefox */
                    input[type="number"] {
                        -moz-appearance: textfield;
                    }
                    `}
            </style>

            <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9]">
                <div className="section-header px-4 py-2">Fee Break-down</div>
                <div className="px-4 py-4 space-y-1">
                    {renderSingleFieldRow("367")}
                    {renderBrokerRow()}
                    {renderCreditRow()}
                    {renderPaidToRow("154", "NEWHUD.X1045", "155")}
                    {renderPaidToRow("1627", "NEWHUD.X1046", "1625")}
                    {renderPaidToRow("1838", "NEWHUD.X1047", "1839")}
                    {renderPaidToRow("1841", "NEWHUD.X1048", "1842")}
                    {renderPaidToRow("NEWHUD.X126", "NEWHUD.X1050", "NEWHUD.X136")}
                    {renderPaidToRow("NEWHUD.X129", "NEWHUD.X1053", "NEWHUD.X139")}
                    {renderPaidToRow("371", "NEWHUD.X1056", "372")}
                    {renderInterestCalcRow()}
                    {renderInterestRangeRow()}
                    {renderLOCompRow()}
                </div>
            </div>
        </>
    );
};

export default FeeBreakdownControls;
